import React, { useState } from 'react';

// One field/entity's fill for the selected run. History splits by TIMING — "before
// this run" (filled by your ATS OR an earlier enrich run) vs "added this run" — which
// is deliberately NOT the Quality tab's split (that one is by SOURCE: from-ATS vs
// added-by-SignalCue, all-time). Because those answer different questions, the leading
// band uses a distinct steel-blue (not the Quality source-amber) so the two tabs don't
// look like they should reconcile. The shared green/missing/can't-enrich bands keep the
// two views feeling like one system.
const fmt = (n) => Number(n || 0).toLocaleString('en-US');

export const RUN_BANDS = {
  carried: { c: '#4f6d99', ink: '#ffffff', label: 'Before this run', hint: 'filled before this run — from your ATS or an earlier enrich run' },
  added: { c: '#1baf7a', ink: '#ffffff', label: 'Added this run', hint: 'values this run filled' },
  missing: { c: '#d6d5cf', ink: '#26251f', label: 'Still missing', hint: 'empty — can still be filled' },
  exhausted: { c: '#e79797', ink: '#6e2020', label: "Can't enrich", hint: 'searched, nothing verifiable found' },
  // Shared with the Quality tab's swatch (QualityOverview PROV.source_owned) so the two
  // views read as one system: fields SignalCue doesn't enrich (name/email/phone/
  // legal_name) — owned by the source ATS, not a fillable gap or a can't-enrich dead-end.
  source_owned: { c: '#9fb3c8', ink: '#22303f', label: 'Not enriched', hint: "SignalCue doesn't enrich this — it's foundational identity or sensitive data that comes from your ATS" }
};
const ORDER = ['carried', 'added', 'missing', 'exhausted', 'source_owned'];

export default function RunCompositionBar({ carried = 0, addedThisRun = 0, exhausted = 0, sourceOwned = 0, total = 0, height = 22, showCounts = true, minLabelPct = 9 }) {
  // Hooks must run unconditionally, before any early return (rules-of-hooks).
  const [hover, setHover] = useState(null);
  const clear = (k) => setHover((h) => (h === k ? null : h));

  // Without a total we can't know the proportions — render a neutral track rather than
  // letting one segment overflow to full width (which would misrepresent the run).
  if (!(total > 0)) {
    return <div className="rounded" style={{ height, background: RUN_BANDS.missing.c }} role="img" aria-label="breakdown unavailable" />;
  }
  // Both exhausted (can't-enrich) AND source_owned (not-enriched) are carved OUT of the
  // grey gap, mirroring QualityOverview.barValue, so the bands re-sum to `total`.
  const missing = Math.max(0, total - carried - addedThisRun - exhausted - sourceOwned);
  const vals = { carried, added: addedThisRun, missing, exhausted, source_owned: sourceOwned };

  return (
    <div className="relative">
      <div className="flex gap-0.5 rounded overflow-hidden" style={{ height, background: RUN_BANDS.missing.c }}>
        {ORDER.map((k) => {
          const v = vals[k];
          if (v <= 0) return null;
          const p = (v / total) * 100;
          const band = RUN_BANDS[k];
          return (
            <div
              key={k}
              tabIndex={0}
              aria-label={`${band.label} — ${fmt(v)} of ${fmt(total)} (${p.toFixed(1)}%)`}
              className="h-full flex items-center justify-center min-w-0 cursor-default focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
              style={{ flex: `0 0 max(${p}%, 3px)`, background: band.c }}
              onMouseEnter={() => setHover(k)}
              onMouseLeave={() => clear(k)}
              onFocus={() => setHover(k)}
              onBlur={() => clear(k)}
            >
              {showCounts && p > minLabelPct && (
                <span className="text-[11px] font-semibold whitespace-nowrap tabular-nums" style={{ color: band.ink }}>{fmt(v)}</span>
              )}
            </div>
          );
        })}
      </div>
      {hover && (
        <div role="tooltip" className="absolute bottom-full left-0 mb-1.5 z-20 w-max max-w-[16rem] rounded-lg bg-gray-900 px-3 py-2 text-[11px] leading-relaxed shadow-lg pointer-events-none">
          <div className="font-semibold" style={{ color: RUN_BANDS[hover].c }}>{RUN_BANDS[hover].label}</div>
          <div className="text-gray-200 tabular-nums">{fmt(vals[hover])} of {fmt(total)} ({((vals[hover] / total) * 100).toFixed(1)}%)</div>
          <div className="text-gray-300">{RUN_BANDS[hover].hint}</div>
        </div>
      )}
    </div>
  );
}
